import { useBonusDispatchHistory, useInstanceDispatchHistory } from '@/hooks/use-bonuses'
import { StatusBadge } from './StatusBadge'
import type { BonusDispatchStatus } from '@/types'

interface DispatchTimelineProps {
  bonusId?: string
  instanceId?: string
}

export function DispatchTimeline({ bonusId, instanceId }: DispatchTimelineProps) {
  // Use instance hook if instanceId provided, otherwise use legacy bonusId hook
  const bonusQuery = useBonusDispatchHistory(bonusId || '', !!bonusId && !instanceId)
  const instanceQuery = useInstanceDispatchHistory(instanceId || '', !!instanceId)

  const { data, isLoading } = instanceId ? instanceQuery : bonusQuery

  if (isLoading) {
    return <div className="animate-pulse h-32 bg-gray-100 rounded" />
  }

  if (!data?.logs.length) {
    return (
      <div className="text-center py-8 text-gray-500">
        No dispatch attempts yet
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <h4 className="font-medium text-gray-900">Dispatch History</h4>
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200" />

        {data.logs.map((log) => (
          <div key={log.id} className="relative pl-10 pb-6">
            {/* Timeline dot */}
            <div
              className={`absolute left-2.5 w-3 h-3 rounded-full border-2 border-white ${
                log.dispatch_status === 'accepted' ? 'bg-success-500' :
                log.dispatch_status === 'rejected' ? 'bg-danger-500' :
                log.dispatch_status === 'failed' ? 'bg-warning-500' :
                'bg-blue-500'
              }`}
            />

            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <StatusBadge status={log.dispatch_status as BonusDispatchStatus} />
                <span className="text-xs text-gray-500">
                  {formatTimestamp(log.initiated_at)}
                </span>
              </div>

              {log.http_status && (
                <div className="text-sm text-gray-600">
                  HTTP {log.http_status} - {log.response_time_ms}ms
                </div>
              )}

              {log.request_body && (() => {
                try {
                  const body = JSON.parse(log.request_body)
                  return body.trace_id ? (
                    <div className="text-xs text-gray-500 mt-1 font-mono">
                      Trace: {body.trace_id}
                    </div>
                  ) : null
                } catch { return null }
              })()}

              {log.error_message && (
                <div className="text-sm text-danger-600 mt-1">
                  {log.error_message}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function formatTimestamp(unix: number): string {
  return new Date(unix * 1000).toLocaleString()
}
